import {
  Button,
  Divider,
  Fab,
  Grid,
  IconButton,
  Paper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles,
  useTheme,
  withStyles
} from '@material-ui/core/styles';
import {
  ArrowForwardRounded,
  CheckCircleOutlineRounded,
  CloudUploadOutlined,
  CloudUploadRounded,
  EditRounded,
  ErrorOutlineRounded,
  HourglassEmptyRounded,
  LaunchRounded,
  LinkRounded,
  LocalShippingOutlined,
  LocalShippingRounded,
  RemoveCircleOutlineRounded
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, useParams } from 'react-router-dom';
import { ProjectDocumentRevisionFile } from '../../api/project-document-revision-files';
import { getProjectDocumentRevisionCoversheets } from '../../api/project-document-revisions';
import { RootState } from '../../app/store';
import DocRevisionInfoCard from '../../components/DocRevisionInfoCard';
import DocumentCoversheetFile from '../../components/DocumentCoversheetFile';
import DocumentFile from '../../components/DocumentFile';
import DocumentFlow from '../../components/DocumentFlow';
import DownloadFile from '../../components/DownloadFile';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import RevisionNote from '../../components/RevisionNote';
import UploadFile from '../../components/UploadFile';
import UploadLink from '../../components/UploadLink';
import { ENABLE_ANNOTATION } from '../../constants';
import checkWebUrl from '../../functions/checkWebUrl';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { createProjectDocumentRevisionFilesAsync } from '../ProjectDocumentRevisionFile/projectDocumentRevisionFileSlice';
import { getProjectDocumentRevisionAsync } from './projectDocumentRevisionSlice';

export enum WorkflowStatusLabel {
  InProgress = 1,
  Completed = 2,
  Rejected = 3,
  Canceled = 4
}

enum OrderWorkflowStepLabel {
  FinalApprover = 6
}

enum StepWorkflowLabel {
  UploadFile = 0,
  WaitForIssue = 1,
  InProgress = 2,
  RejectedCanceledCompleted = 3
}

interface StepWorkflow {
  label: string;
  completed: boolean;
  display: boolean;
  icon: JSX.Element;
  bgColor?: 'error' | 'neutral';
}

export default function DocumentRevisionView() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.userProfile);

  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const coversheetName = `${selectedProjectDocument?.no}_${selectedProjectDocumentRevision?.revision.name}_${selectedProjectDocument?.name}.pdf`;

  const documentRevisionFiles =
    selectedProjectDocumentRevision?.projectDocumentRevisionFiles ?? [];
  const documentWorkflows =
    selectedProjectDocumentRevision?.projectDocumentWorkflows ?? [];

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const params = useParams<{ revId: string }>();
  const revId = Number.parseInt(params.revId, 10);

  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [openDownload, setOpenDownload] = useState<boolean>(false);
  const [openLink, setOpenLink] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [currentWorkflow] = selectedProjectDocumentRevision
    ? selectedProjectDocumentRevision.projectDocumentWorkflows
    : [];
  const isDocCreator: boolean =
    selectedProjectDocument?.documentCreatorId === user?.id;
  const disabledModifiedFiles: boolean =
    currentWorkflow &&
    ((currentWorkflow.workflowStatusId === WorkflowStatusLabel.InProgress &&
      currentWorkflow.currentWorkflowStep.workflowStep.order >
        OrderWorkflowStepLabel.FinalApprover) ||
      currentWorkflow.workflowStatusId === WorkflowStatusLabel.Completed);

  const hasFileOrLink = Boolean(
    documentRevisionFiles.length > 0 ||
      (selectedProjectDocumentRevision &&
        selectedProjectDocumentRevision.linkUrl)
  );
  const showNewWorkflowButton = !(
    currentWorkflow &&
    (currentWorkflow.workflowStatusId === WorkflowStatusLabel.InProgress ||
      currentWorkflow.workflowStatusId === WorkflowStatusLabel.Completed)
  );

  const stepWorkflows: StepWorkflow[] = [
    {
      label: t('documentRevisionView.workflowSteps.uploadFile') as string,
      completed: hasFileOrLink,
      display: true,
      icon: <CloudUploadOutlined fontSize="small" />
    },
    {
      label: t('documentRevisionView.workflowSteps.startWorkflow') as string,
      completed: Boolean(currentWorkflow),
      display: true,
      icon: <LocalShippingOutlined fontSize="small" />
    },
    {
      label: t('documentRevisionView.workflowSteps.reviewDocument') as string,
      completed:
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Rejected ||
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Canceled ||
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Completed,
      display: true,
      icon: <HourglassEmptyRounded fontSize="small" />
    },
    {
      label: t('documentRevisionView.workflowSteps.rejected') as string,
      completed: true,
      display:
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Rejected,
      icon: <ErrorOutlineRounded fontSize="small" />,
      bgColor: 'error'
    },
    {
      label: t('documentRevisionView.workflowSteps.canceled') as string,
      completed: true,
      display:
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Canceled,
      icon: <RemoveCircleOutlineRounded fontSize="small" />,
      bgColor: 'neutral'
    },
    {
      label: t('documentRevisionView.workflowSteps.completed') as string,
      completed:
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Completed,
      display:
        Boolean(!currentWorkflow) ||
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.InProgress ||
        currentWorkflow?.workflowStatusId === WorkflowStatusLabel.Completed,
      icon: <CheckCircleOutlineRounded fontSize="small" />
    }
  ];
  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    if (selectedProjectDocumentRevision) {
      const [currentWorkflow] =
        selectedProjectDocumentRevision.projectDocumentWorkflows;
      if (currentWorkflow) {
        if (
          currentWorkflow.workflowStatusId === WorkflowStatusLabel.InProgress
        ) {
          setActiveStep(StepWorkflowLabel.InProgress);
        } else {
          setActiveStep(StepWorkflowLabel.RejectedCanceledCompleted + 1);
        }
      } else {
        if (hasFileOrLink) {
          setActiveStep(StepWorkflowLabel.WaitForIssue);
        } else {
          setActiveStep(StepWorkflowLabel.UploadFile);
        }
      }
    }
  }, [selectedProjectDocumentRevision, hasFileOrLink]);

  useEffect(() => {
    dispatch(getProjectDocumentRevisionAsync(revId));
  }, []);

  async function onSubmit(files: ProjectDocumentRevisionFile[]) {
    try {
      setLoading(true);
      const result = await dispatch(
        createProjectDocumentRevisionFilesAsync({
          projectDocumentRevisionFiles: files,
          projectDocumentRevisionId: revId
        })
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('documentRevisionView.messages.filesUploadedSuccessfully') as string
        })
      );
      setTimeout(() => {
        setOpenUpload(false);
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <UploadFile
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        multiple
        onSubmit={onSubmit}
        loading={loading}
        canChangePrivacy
        dispatch={dispatch}
      />
      {/* TODO: DownloadFile is used for multiple download but not to implement now */}
      <DownloadFile
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        documentRevisionId={revId}
        renderDocumentRevisionFiles={documentRevisionFiles}
        coversheetName={coversheetName}
      />
      {selectedProjectDocumentRevision && (
        <UploadLink
          open={openLink}
          onClose={() => setOpenLink(false)}
          linkName={selectedProjectDocumentRevision.linkName}
          linkUrl={selectedProjectDocumentRevision.linkUrl}
          documentRevisionId={selectedProjectDocumentRevision.id}
        />
      )}
      <Breadcrumb
        name="revisionName"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id,
          revisionName: `Purpose: ${selectedProjectDocumentRevision?.purpose.abbreviation}, Revision: ${selectedProjectDocumentRevision?.revision.name}`,
          revisionId: selectedProjectDocumentRevision?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('documentRevisionView.title')}
        <Tooltip title={t('documentRevisionView.tooltips.editDocumentRevision') as string} arrow>
          <IconButton
            className={classes.marginIconButton}
            component={RRLink}
            to={`/document-revisions/${revId}/edit`}
            size="small"
          >
            <EditRounded />
          </IconButton>
        </Tooltip>
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && selectedProjectDocumentRevision && (
            <DocRevisionInfoCard
              document={selectedProjectDocument}
              docRevision={selectedProjectDocumentRevision}
            />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.card}>
            <div className={classes.flexAlignCenter}>
              <Typography
                className={classes.flexGrow}
                variant="subtitle1"
                gutterBottom
              >
                {t('documentRevisionView.sections.documentIssuanceProcess')}
              </Typography>
              {currentWorkflow && (
                <Tooltip title={t('documentRevisionView.tooltips.goToCurrentWorkflow') as string} arrow>
                  <IconButton
                    component={RRLink}
                    to={`/approvals/${currentWorkflow.id}`}
                    size="small"
                    edge="end"
                  >
                    <ArrowForwardRounded />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Divider className={classes.divider} />
            <Stepper
              className={classes.stepper}
              alternativeLabel
              nonLinear
              activeStep={activeStep}
              connector={<Connector />}
            >
              {stepWorkflows
                .filter(({ display }) => display)
                .map(({ label, completed, icon, bgColor }) => (
                  <Step key={label} completed={completed}>
                    <StepLabel
                      StepIconComponent={(props) =>
                        StepIcon(props, icon, bgColor)
                      }
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
            </Stepper>
          </Paper>
          <Paper className={classes.card}>
            <div className={classes.flexAlignCenter}>
              <Typography className={classes.flexGrow} variant="subtitle1">
                {t('documentRevisionView.sections.files')}
              </Typography>
              {isDocCreator && (
                <>
                  <Tooltip
                    title={
                      (disabledModifiedFiles
                        ? t('documentRevisionView.tooltips.cannotAttachLink')
                        : t('documentRevisionView.tooltips.attachLink')) as string
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        className={classes.marginRight}
                        size="small"
                        onClick={() => setOpenLink(true)}
                        disabled={disabledModifiedFiles}
                      >
                        <LinkRounded className={classes.rotate45} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setOpenUpload(true)}
                    startIcon={<CloudUploadRounded />}
                    disabled={disabledModifiedFiles}
                  >
                    {t('documentRevisionView.buttons.uploadNewFile')}
                  </Button>
                </>
              )}
            </div>
            <Divider className={classes.divider} />
            {hasFileOrLink && (
              <DocumentCoversheetFile
                coversheetName={coversheetName}
                onPreview={() =>
                  getProjectDocumentRevisionCoversheets({
                    projectDocumentRevisionId: revId,
                    filename: coversheetName,
                    isDownload: false,
                    isPreview: true
                  })
                }
                onDownload={() =>
                  getProjectDocumentRevisionCoversheets({
                    projectDocumentRevisionId: revId,
                    filename: coversheetName
                  })
                }
              />
            )}
            {documentRevisionFiles.length > 0 ? (
              <DocumentFile
                renderDocumentRevisionFiles={documentRevisionFiles}
                projectDocumentRevisionId={revId}
                isDocCreator={isDocCreator}
                disabledModified={disabledModifiedFiles}
                accessLevelName={selectedProjectDocument?.accessControl.name}
                username={
                  user ? `${user.firstName} ${user.lastName}` : undefined
                }
                enableAnnotation={
                  ENABLE_ANNOTATION &&
                  !!selectedProject?.projectNo &&
                  (
                    process.env.REACT_APP_PILOT_ANNOTATION_PROJECT_NO?.split(
                      ','
                    ).filter(Boolean) ?? []
                  ).includes(selectedProject.projectNo)
                }
              />
            ) : (
              !selectedProjectDocumentRevision?.linkUrl && (
                <div className={classes.fileNotFound}>{t('documentRevisionView.messages.fileNotFound')}</div>
              )
            )}
            {selectedProjectDocumentRevision &&
              selectedProjectDocumentRevision.linkUrl && (
                <div className={classes.linkBox}>
                  <Typography
                    className={classes.link}
                    component="a"
                    href={checkWebUrl(selectedProjectDocumentRevision.linkUrl)}
                    target="_blank"
                    variant="body1"
                    display="inline"
                  >
                    {selectedProjectDocumentRevision.linkName
                      ? selectedProjectDocumentRevision.linkName
                      : selectedProjectDocumentRevision.linkUrl}
                    <LaunchRounded color="action" fontSize="small" />
                  </Typography>
                </div>
              )}
            {isDocCreator ? (
              hasFileOrLink ? (
                <Alert className={classes.marginTop} severity="info">
                  {t('documentRevisionView.alerts.cannotUploadDeleteFiles.info')}{' '}
                  <b>{t('documentRevisionView.alerts.cannotUploadDeleteFiles.finalApprover')}</b>{' '}
                  {t('documentRevisionView.alerts.cannotUploadDeleteFiles.stepOrStatusIs')}{' '}
                  <b>{t('documentRevisionView.alerts.cannotUploadDeleteFiles.completed')}</b>
                </Alert>
              ) : (
                <Alert className={classes.marginTop} severity="error">
                  {t('documentRevisionView.alerts.pleaseUploadFile.please')}{' '}
                  <b>{t('documentRevisionView.alerts.pleaseUploadFile.uploadFile')}</b>{' '}
                  {t('documentRevisionView.alerts.pleaseUploadFile.or')}{' '}
                  <b>{t('documentRevisionView.alerts.pleaseUploadFile.attachLink')}</b>{' '}
                  {t('documentRevisionView.alerts.pleaseUploadFile.beforeStarting')}
                </Alert>
              )
            ) : (
              <Alert className={classes.marginTop} severity="warning">
                {t('documentRevisionView.alerts.onlyDocumentCreator.only')}{' '}
                <b>{t('documentRevisionView.alerts.onlyDocumentCreator.documentCreator')}</b>{' '}
                {t('documentRevisionView.alerts.onlyDocumentCreator.canUploadFile')}
              </Alert>
            )}
          </Paper>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('documentRevisionView.sections.workflows')}
            </Typography>
            <Divider className={classes.divider} />
            {documentWorkflows.length > 0 && selectedProjectDocumentRevision ? (
              <DocumentFlow
                documentWorkflows={documentWorkflows}
                isDocCreator={isDocCreator}
                planIssueDate={selectedProjectDocumentRevision.planIssueDate}
              />
            ) : (
              <div className={classes.fileNotFound}>
                {t('documentRevisionView.messages.workflowNotStarted')}
              </div>
            )}
            {isDocCreator ? (
              hasFileOrLink && (
                <Alert className={classes.marginTop} severity="info">
                  {t('documentRevisionView.alerts.cannotStartNewWorkflow.cannotStart')}{' '}
                  <b>{t('documentRevisionView.alerts.cannotStartNewWorkflow.completed')}</b>
                </Alert>
              )
            ) : (
              <Alert className={classes.marginTop} severity="warning">
                {t('documentRevisionView.alerts.onlyDocumentCreatorWorkflow.only')}{' '}
                <b>{t('documentRevisionView.alerts.onlyDocumentCreatorWorkflow.documentCreator')}</b>{' '}
                {t('documentRevisionView.alerts.onlyDocumentCreatorWorkflow.canCreateWorkflow')}
              </Alert>
            )}
          </Paper>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('documentRevisionView.sections.revisionNote')}
            </Typography>
            <Divider className={classes.divider} />
            {selectedProjectDocumentRevision && (
              <RevisionNote
                value={selectedProjectDocumentRevision.note}
                documentRevisionId={selectedProjectDocumentRevision.id}
                canEdit={isDocCreator}
                disabledModified={disabledModifiedFiles}
              />
            )}
            {isDocCreator ? (
              hasFileOrLink && (
                <Alert className={classes.marginTop} severity="info">
                  {t('documentRevisionView.alerts.cannotEditNote.cannotAddEdit')}{' '}
                  <b>{t('documentRevisionView.alerts.cannotEditNote.finalApprover')}</b>{' '}
                  {t('documentRevisionView.alerts.cannotEditNote.stepOrStatusIs')}{' '}
                  <b>{t('documentRevisionView.alerts.cannotEditNote.completed')}</b>
                </Alert>
              )
            ) : (
              <Alert className={classes.marginTop} severity="warning">
                {t('documentRevisionView.alerts.onlyDocumentCreatorNote.only')}{' '}
                <b>{t('documentRevisionView.alerts.onlyDocumentCreatorNote.documentCreator')}</b>{' '}
                {t('documentRevisionView.alerts.onlyDocumentCreatorNote.canAddEditNote')}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
      {isDocCreator && showNewWorkflowButton && (
        <Zoom
          in
          timeout={transitionDuration}
          style={{
            transitionDelay: `${transitionDuration.exit}ms`
          }}
          unmountOnExit
        >
          <Fab
            className={classes.actionIcon}
            component={RRLink}
            to={`/document-workflows/create?documentRevisionId=${revId}`}
            variant="extended"
            color="primary"
            aria-label="create-flow"
            disabled={!hasFileOrLink}
          >
            <LocalShippingRounded className={classes.extendedIcon} />
            {t('documentRevisionView.buttons.startDocumentWorkflow')}
          </Fab>
        </Zoom>
      )}
    </div>
  );
}

function StepIcon(
  props: StepIconProps,
  icon: JSX.Element,
  bgColor?: 'error' | 'neutral'
) {
  const classes = useStepIconStyles();
  const { active, completed } = props;

  let completedClass;

  switch (bgColor) {
    case 'error':
      completedClass = classes.error;
      break;
    case 'neutral':
      completedClass = classes.neutral;
      break;
    default:
      completedClass = classes.success;
  }

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [completedClass]: completed
      })}
    >
      {icon}
    </div>
  );
}

const Connector = withStyles((theme: Theme) =>
  createStyles({
    alternativeLabel: {
      top: 16
    },
    active: {
      '& $line': {
        backgroundImage: `linear-gradient( 90deg,${theme.palette.success.main} 0%,${theme.palette.primary.main} 100%)`
      }
    },
    completed: {
      '& $line': {
        backgroundColor: theme.palette.success.main
      }
    },
    line: {
      height: 2,
      border: 0,
      backgroundColor: theme.palette.divider,
      borderRadius: 1
    }
  })
)(StepConnector);

const useStepIconStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.divider,
      zIndex: 1,
      color: '#fff',
      width: 32,
      height: 32,
      display: 'flex',
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center'
    },
    active: {
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, .25)'
    },
    success: {
      backgroundColor: theme.palette.success.main
    },
    error: {
      backgroundColor: theme.palette.error.main
    },
    neutral: {
      backgroundColor: theme.palette.text.hint
    }
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    stepper: {
      padding: theme.spacing(0, 0, 1, 0)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    extendedIcon: {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(1.5)
    },
    actionIcon: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      paddingRight: theme.spacing(3)
    },
    flexGrow: {
      flexGrow: 1
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    fileNotFound: {
      padding: theme.spacing(1, 2, 3, 2),
      textAlign: 'center'
    },
    rotate45: {
      rotate: '-45deg'
    },
    marginIconButton: {
      margin: theme.spacing(-1, 0, -1, 1)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginTop: {
      marginTop: theme.spacing(1)
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.dark
      },
      wordBreak: 'break-all'
    },
    linkBox: {
      borderRadius: theme.spacing(1),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      padding: theme.spacing(0.8, 2)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    }
  })
);
